import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Radio,
  FormControlLabel,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Styled Components
const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 20,
    maxWidth: "400px",
    width: "100%",
    maxHeight: "90vh",
    margin: theme.spacing(2),
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 3),
  borderBottom: "1px solid #e5e7eb",
}));

const DialogTitleStyled = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 600,
  color: "#7C3AED",
  textTransform: "lowercase",
}));

const PaymentOptions = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const PaymentOption = styled(FormControlLabel)(({ theme, isSelected, disabled }) => ({
  margin: 0,
  padding: theme.spacing(1, 0),
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  "& .MuiFormControlLabel-label": {
    fontSize: "16px",
    fontWeight: isSelected ? 600 : 400,
    color: disabled ? "#9ca3af" : (isSelected ? "#000000" : "#6b7280"),
  },
  "& .MuiRadio-root": {
    padding: theme.spacing(0.5),
    "&.Mui-checked": {
      color: disabled ? "#9ca3af" : "#000000",
    },
    "&.Mui-disabled": {
      color: "#9ca3af",
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#0085FF",
  color: "white",
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "lowercase",
  "&:hover": {
    backgroundColor: "#0066CC",
  },
  "&:disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: "#6b7280",
  "&:hover": {
    backgroundColor: "#f3f4f6",
  },
}));

/**
 * PaymentDialog Component
 * 
 * This component displays a payment dialog with bill splitting options
 * similar to the design shown in the image.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Object} props.paymentData - Payment data containing amount and options
 * @param {string} props.orderId - Order ID for payment initiation
 */
const PaymentDialog = ({ open, onClose, paymentData = null, orderId = null, onPaymentSubmit, groupMemberCount = 1, messageType = null }) => {
  // Initialize selected option based on groupMemberCount
  // When there's only one customer, default to "individual" (pay for yourself)
  const getInitialOption = () => {
    if (groupMemberCount <= 1) {
      return "individual";
    }
    return messageType === "payment_request_bill" ? "pay_all" : "pay_for_all";
  };

  const [selectedOption, setSelectedOption] = useState(getInitialOption());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const actualOrderId = orderId?._id || orderId;

  const handleOptionChange = (option) => {
    setSelectedOption(option);
  };

  // Update selected option when dialog opens, groupMemberCount, or messageType changes
  useEffect(() => {
    if (open) {
      // When there's only one customer, default to "individual" (pay for yourself)
      if (groupMemberCount <= 1) {
        setSelectedOption("individual");
      } else {
        // When there are multiple customers, use default options
        if (messageType === "payment_request_bill") {
          setSelectedOption("pay_all");
        } else {
          setSelectedOption("pay_for_all");
        }
      }
    }
  }, [open, groupMemberCount, messageType]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let paymentType;

      if (messageType === "payment_request_bill") {
        // For payment request bill, map to appropriate payment types
        switch (selectedOption) {
          case "pay_all":
            paymentType = "pay_for_all";
            break;
          case "split_per_person":
            paymentType = "split_evenly";
            break;
          case "individual":
            paymentType = "individual";
            break;
          default:
            paymentType = "individual";
        }
      } else {
        // For other payment types, use existing logic
        paymentType = selectedOption === "pay_for_all" ? "pay_for_all" : "individual";
      }

      // Use callback if provided
      if (onPaymentSubmit) {
        await onPaymentSubmit(paymentType);
      }

      // Close dialog after selection
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Payment options configuration based on message type
  const getPaymentOptions = () => {
    if (messageType === "payment_request_bill") {
      return [
        {
          value: "pay_all",
          label: "pay all",
          disabled: groupMemberCount <= 1, // Disabled when only one regular user
        },
        {
          value: "split_per_person",
          label: "split per person",
          disabled: groupMemberCount <= 1, // Disabled when only one regular user
        },
        {
          value: "individual",
          label: "pay for yourself",
          disabled: false, // Always available
        },
      ];
    } else {
      // Default options for other payment types
      return [
        {
          value: "pay_for_all",
          label: "pay for all",
          disabled: groupMemberCount <= 1,
        },
        {
          value: "individual",
          label: "pay for yourself",
          disabled: false,
        },
      ];
    }
  };

  const paymentOptions = getPaymentOptions();

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader>
        <Box width="100%" textAlign="center">
          <DialogTitleStyled>payment</DialogTitleStyled>
        </Box>
        <CloseButton onClick={onClose} size="small">
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <DialogContent sx={{ p: 0 }}>
        <PaymentOptions>
          {paymentOptions.map((option) => (
            <PaymentOption
              key={option.value}
              disabled={option.disabled}
              isSelected={selectedOption === option.value}
              control={
                <Radio
                  checked={selectedOption === option.value}
                  onChange={() => !option.disabled && handleOptionChange(option.value)}
                  disabled={option.disabled}
                />
              }
              label={
                <Typography
                  sx={{
                    color: option.disabled ? "#9ca3af" : "#000000",
                    fontWeight: selectedOption === option.value ? 600 : 400,
                  }}
                >
                  {option.label}
                </Typography>
              }
            />
          ))}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <SubmitButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isSubmitting ? "submitting..." : "submit"}
            </SubmitButton>
          </Box>
        </PaymentOptions>
      </DialogContent>
    </CustomDialog>
  );
};

export default PaymentDialog;
